import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import mermaid from 'mermaid';
import ModalWrapper from '../../components/ModalWrapper';

// Initialize mermaid once with dark theme
let initialized = false;
function ensureInit() {
  if (initialized) return;
  initialized = true;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    themeVariables: {
      primaryColor: '#9C27B0',
      primaryTextColor: '#fff',
      primaryBorderColor: 'rgba(156, 39, 176, 0.5)',
      lineColor: 'rgba(255, 255, 255, 0.3)',
      secondaryColor: 'rgba(76, 175, 80, 0.3)',
      tertiaryColor: 'rgba(33, 150, 243, 0.2)',
      background: '#1a1a2e',
      mainBkg: '#1a1a2e',
      nodeBorder: 'rgba(255, 255, 255, 0.2)',
      clusterBkg: 'rgba(255, 255, 255, 0.05)',
      titleColor: '#fff',
      edgeLabelBackground: '#1a1a2e',
    },
    flowchart: { curve: 'basis', padding: 12 },
    fontSize: 12,
    // 'strict' keeps mermaid's own sanitizer on: the chart source is AI chat
    // output built from workshop and RAG content, i.e. untrusted (#935).
    securityLevel: 'strict',
    suppressErrorRendering: true,
  });
}

// Serialize renders — mermaid.render() is NOT concurrency-safe
let renderQueue = Promise.resolve();

// ─── Inline preview (small, in chat bubble) ──────────────────────

const Preview = styled.div`
  margin: 8px 0;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 8px;
  position: relative;
  cursor: pointer;
  max-height: 200px;

  &:hover .expand-hint { opacity: 1; }
  &:hover { background: rgba(0, 0, 0, 0.3); }

  svg { max-width: 100%; height: auto; }
`;

const ExpandHint = styled.div`
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(76, 175, 80, 0.8);
  color: white;
  font-size: 10px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
`;

// ─── Modal (80vw × 80vh, centered, closeable) ───────────────────

const Modal = styled.div`
  width: 80vw;
  height: 80vh;
  background: #1a1a2e;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`;

const ModalTitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const ModalControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ControlButton = styled.button`
  height: 28px;
  min-width: 28px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

const CloseButton = styled(ControlButton)`
  margin-left: 8px;
  &:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.3);
    color: #ef4444;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  user-select: none;
  touch-action: none;

  svg { max-width: none; height: auto; }
`;

const ZoomLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  min-width: 36px;
  text-align: center;
`;

const ErrorText = styled.div`
  font-size: 11px;
  color: rgba(239, 68, 68, 0.8);
  padding: 8px;
`;

/**
 * MermaidBlock — renders a mermaid diagram as SVG.
 * Shows a small preview in chat; click to open a large modal.
 */
const MermaidBlock = ({ chart }) => {
  const previewRef = useRef(null);
  const [error, setError] = useState(null);
  const [svgHtml, setSvgHtml] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
  const modalBodyRef = useRef(null);
  const svgSizeRef = useRef({ w: 0, h: 0 }); // intrinsic SVG dimensions

  // Render the chart SVG
  useEffect(() => {
    if (!chart?.trim()) return;
    ensureInit();

    let cancelled = false;
    const id = `mermaid-${crypto.randomUUID().slice(0, 8)}`;

    renderQueue = renderQueue.then(async () => {
      if (cancelled) return;
      try {
        const { svg } = await mermaid.render(id, chart.trim());
        if (!cancelled) {
          // Extract intrinsic size from the SVG markup before any CSS transforms
          const parser = new DOMParser();
          const doc = parser.parseFromString(svg, 'image/svg+xml');
          const svgEl = doc.querySelector('svg');
          if (svgEl) {
            // Try viewBox first (most reliable), then width/height attributes, then style
            const vb = svgEl.getAttribute('viewBox');
            if (vb) {
              const parts = vb.split(/[\s,]+/).map(Number);
              svgSizeRef.current = { w: parts[2], h: parts[3] };
            } else {
              const w = parseFloat(svgEl.getAttribute('width')) || 0;
              const h = parseFloat(svgEl.getAttribute('height')) || 0;
              svgSizeRef.current = { w, h };
            }
            // Also check style max-width (mermaid often sets this)
            if (!svgSizeRef.current.w) {
              const maxW = svgEl.style?.maxWidth;
              if (maxW) svgSizeRef.current.w = parseFloat(maxW);
            }
          }
          setSvgHtml(svg);
          setError(null);
          if (previewRef.current) previewRef.current.innerHTML = svg;
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Mermaid render failed');
        document.getElementById(`d${id}`)?.remove();
      }
    });

    return () => { cancelled = true; };
  }, [chart]);

  const openModal = useCallback(() => {
    if (!svgHtml) return;
    setPan({ x: 0, y: 0 });
    setZoom(1); // temporary, fitToExtents runs after mount
    setModalOpen(true);
  }, [svgHtml]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const fitToExtents = useCallback(() => {
    if (!modalBodyRef.current) return;
    const PAD = 50;
    const body = modalBodyRef.current;
    const bodyW = body.clientWidth - PAD * 2;
    const bodyH = body.clientHeight - PAD * 2;
    const { w: svgW, h: svgH } = svgSizeRef.current;
    if (svgW > 0 && svgH > 0 && bodyW > 0 && bodyH > 0) {
      setZoom(+Math.min(bodyW / svgW, bodyH / svgH).toFixed(3));
    }
    setPan({ x: 0, y: 0 });
  }, []);

  // Fit chart to modal extents on open
  useEffect(() => {
    if (!modalOpen) return;
    // Wait one frame for the modal to layout
    requestAnimationFrame(fitToExtents);
  }, [modalOpen, fitToExtents]);

  const adjustZoom = useCallback((delta) => {
    setZoom(z => Math.min(20, Math.max(0.1, +(z + delta).toFixed(2))));
  }, []);

  // Drag to pan
  const onPointerDown = useCallback((e) => {
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [pan]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    setPan({ x: d.startPanX + (e.clientX - d.startX), y: d.startPanY + (e.clientY - d.startY) });
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current.dragging = false;
  }, []);

  // Keyboard shortcuts when modal is open
  useEffect(() => {
    if (!modalOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeModal();
      else if (e.key === '+' || e.key === '=') adjustZoom(0.25);
      else if (e.key === '-') adjustZoom(-0.25);
      else if (e.key === '0') fitToExtents();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [modalOpen, closeModal, adjustZoom, fitToExtents]);

  // Scroll-to-zoom in modal — smooth for trackpad, works for mouse wheel too
  useEffect(() => {
    if (!modalOpen || !modalBodyRef.current) return;
    const el = modalBodyRef.current;
    const handleWheel = (e) => {
      e.preventDefault();
      // Clamp deltaY: trackpad sends small values (1-5), mouse wheel sends large (50-120)
      const clamped = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY), 30);
      setZoom(z => {
        const factor = 1 - clamped * 0.003; // ~0.3% per pixel of delta
        return Math.min(20, Math.max(0.1, +(z * factor).toFixed(3)));
      });
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [modalOpen]);

  if (error) {
    return (
      <Preview>
        <ErrorText>Diagram error: {error}</ErrorText>
        <pre style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: 0, whiteSpace: 'pre-wrap' }}>{chart}</pre>
      </Preview>
    );
  }

  return (
    <>
      <Preview ref={previewRef} onClick={openModal}>
        <ExpandHint className="expand-hint">Avaa isompana</ExpandHint>
      </Preview>

      {modalOpen && svgHtml && createPortal(
        <ModalWrapper visible={modalOpen} onClose={closeModal} showCloseButton={false} zIndex={10000} blur={0} opacity={0.6}>
          <Modal>
            <ModalHeader>
              <ModalTitle>Mermaid-kaavio</ModalTitle>
              <ModalControls>
                <ControlButton onClick={() => adjustZoom(-0.25)} title="Zoom out (-)">−</ControlButton>
                <ZoomLabel>{Math.round(zoom * 100)}%</ZoomLabel>
                <ControlButton onClick={() => adjustZoom(0.25)} title="Zoom in (+)">+</ControlButton>
                <ControlButton onClick={fitToExtents} title="Sovita (0)">Sovita</ControlButton>
                <CloseButton onClick={closeModal} title="Sulje (Esc)">&times;</CloseButton>
              </ModalControls>
            </ModalHeader>
            <ModalBody
              ref={modalBodyRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              style={{ cursor: dragRef.current.dragging ? 'grabbing' : 'grab' }}
            >
              <div
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  transition: dragRef.current.dragging ? 'none' : 'transform 0.15s ease',
                }}
                dangerouslySetInnerHTML={{ __html: svgHtml }}
              />
            </ModalBody>
          </Modal>
        </ModalWrapper>,
        document.body
      )}
    </>
  );
};

export default MermaidBlock;
